import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const CustomViewSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  module: z.string(),
  section: z.string(),
  config: z.record(z.string(), z.unknown()),
  displayOrder: z.int().nonnegative(),
  title: z.string(),
  creatorName: z.string().nullable(),
  creatorId: UUIDSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const CreateCustomViewInputSchema = z.object({
  module: z.string(),
  section: z.string(),
  config: z.record(z.string(), z.unknown()),
  title: z.string(),
  displayOrder: z.int().nonnegative().optional(),
});

export const ListCustomViewsInputSchema = z.object({
  module: z.string(),
  section: z.string(),
});

export const ListCustomViewsResponseSchema = z.object({
  views: z.array(CustomViewSchema),
});

export const UpdateCustomViewInputSchema = z.object({
  id: UUIDSchema,
  data: z
    .object({
      config: z.record(z.string(), z.unknown()).optional(),
      title: z.string().optional(),
      displayOrder: z.int().nonnegative().optional(),
    })
    .refine(
      (data) => Object.keys(data).length > 0,
      "At least one field must be provided",
    ),
});

export const DeleteCustomViewInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteCustomViewResponseSchema = z.object({
  success: z.boolean(),
  module: z.string().nullable(),
  section: z.string().nullable(),
});

export const ReorderCustomViewsInputSchema = z.object({
  module: z.string(),
  section: z.string(),
  orderedIds: z.array(UUIDSchema),
});

export const ReorderCustomViewsResponseSchema = z.object({
  success: z.boolean(),
});

export const customViewsContracts = {
  create: oc.input(CreateCustomViewInputSchema).output(CustomViewSchema).route({
    method: "POST",
    summary: "Create a saved view",
    description:
      "Saves a named view configuration (filters, columns, layout) for a module section, shared across the organization.",
  }),
  list: oc
    .input(ListCustomViewsInputSchema)
    .output(ListCustomViewsResponseSchema)
    .route({
      method: "GET",
      summary: "List saved views for a page section",
      description:
        "Returns the organization's saved views for one module section in display order, with creator name and configuration.",
    }),
  update: oc
    .input(UpdateCustomViewInputSchema)
    .output(CustomViewSchema.nullable())
    .route({
      method: "PUT",
      summary: "Update a saved view",
      description:
        "Changes a saved view's title, configuration or display position. Returns null when the view is not found.",
    }),
  delete: oc
    .input(DeleteCustomViewInputSchema)
    .output(DeleteCustomViewResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete a saved view",
      description:
        "Permanently removes a saved view and returns the module and section it belonged to.",
    }),
  reorder: oc
    .input(ReorderCustomViewsInputSchema)
    .output(ReorderCustomViewsResponseSchema)
    .route({
      method: "POST",
      summary: "Reorder saved views",
      description:
        "Sets the display order of a module section's saved views to match the given list of view IDs.",
    }),
} as const;

export type CustomView = z.infer<typeof CustomViewSchema>;
export type CreateCustomViewInput = z.infer<typeof CreateCustomViewInputSchema>;
export type ListCustomViewsInput = z.infer<typeof ListCustomViewsInputSchema>;
export type ListCustomViewsResponse = z.infer<
  typeof ListCustomViewsResponseSchema
>;
export type UpdateCustomViewInput = z.infer<typeof UpdateCustomViewInputSchema>;
export type DeleteCustomViewInput = z.infer<typeof DeleteCustomViewInputSchema>;
export type DeleteCustomViewResponse = z.infer<
  typeof DeleteCustomViewResponseSchema
>;
export type ReorderCustomViewsInput = z.infer<
  typeof ReorderCustomViewsInputSchema
>;
export type ReorderCustomViewsResponse = z.infer<
  typeof ReorderCustomViewsResponseSchema
>;
